import React, { ReactElement, useState } from 'react';
import { Box, Popover, TextField } from '@mui/material';
import { Dayjs } from 'dayjs';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { ReactComponent as CalendarIcon } from 'assets/images/calendar-button.svg';
import { AppPrimaryButtonOutlined } from 'shared/components/AppComponents';

interface Props {
  value: [Dayjs | null, Dayjs | null];
  onChange: (value: [Dayjs | null, Dayjs | null]) => void;
}

const CustomDateRangePicker = ({ value, onChange }: Props): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleStartDateChange = (newValue: Dayjs | null): void => {
    onChange([newValue, value[1]]);
  };

  const handleEndDateChange = (newValue: Dayjs | null): void => {
    onChange([value[0], newValue]);
    handleClose();
  };

  const formattedDateRange = `${value[0]?.format(
    'MMM DD, YYYY'
  )} - ${value[1]?.format('MMM DD, YYYY')}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <AppPrimaryButtonOutlined
        title={formattedDateRange}
        startIcon={<CalendarIcon />}
        onClick={handleOpen}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={value[0]}
            disableFuture
            onChange={handleStartDateChange}
            renderInput={(params) => <TextField {...params} />}
          />

          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={value[1]}
            disableFuture
            minDate={value[0] || undefined}
            onChange={handleEndDateChange}
            renderInput={(params) => <TextField {...params} />}
          />
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
